import { sortByAPY, sortByUsdValue } from '@/domain/common/sorters'
import { OpenDialogFunction } from '@/domain/hooks/useDialog'
import { Button } from '@/ui/atoms/button/Button'
import { ApyTooltip } from '@/ui/molecules/apy-tooltip/ApyTooltip'
import { ActionsCell } from '@/ui/molecules/data-table/components/ActionsCell'
import { CompactValueCell } from '@/ui/molecules/data-table/components/CompactValueCell'
import { TokenWithLogo } from '@/ui/molecules/data-table/components/TokenWithLogo'
import { ResponsiveDataTable } from '@/ui/organisms/responsive-data-table/ResponsiveDataTable'
import { ApyWithRewardsCell } from '@/features/my-portfolio/components/ApyWithRewardsCell'
import { useMemo } from 'react'
import { ColumnDefinition } from '@/ui/molecules/data-table/types'
import { Borrow } from '../../logic/useMyPortfolio'
import { useIsolatedPairDetails } from '@/features/isolated-details/logic/useIsolatedPairDetails'
import { repayDialogConfig } from '@/features/isolated-dialogs/repay/RepayDialog'
import { Typography } from '@/ui/atoms/typography/Typography'
import { TableCell, TableRow } from '@/ui/atoms/table/Table'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { Address } from 'viem'
import { getDisplayToken } from '@/domain/hooks/useDisplayToken'
import { getChainConfigEntry } from '@/config/chain'
import { useChainId } from 'wagmi'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { cn } from '@/ui/utils/style'
import { usePointsData } from '@/domain/points/pointsData/usePointsData'
import { Panel } from '@/ui/atoms/panel/Panel'

export interface MyBorrowsTableProps {
  assets: Borrow[]
  openDialog: OpenDialogFunction
}

export function MyBorrowsTable({ assets: rawAssets, openDialog }: MyBorrowsTableProps) {
  const assets = rawAssets.map((asset) => ({
    ...asset,
    collateralToken: getDisplayToken(asset.chainId, asset.collateralToken),
    borrowToken: getDisplayToken(asset.chainId, asset.borrowToken),
  }))
  const chainId = useChainId()
  const chainConfig = getChainConfigEntry(chainId).isolatedPartnerPoints
  const { getByAddress, error: pointsDataError, isLoading: isPointsDataLoading } = usePointsData()
  const hasPointsError = !isPointsDataLoading && !!pointsDataError?.message

  const columnDefinition = useMemo<{ [key: string]: ColumnDefinition<Borrow> }>(
    () => ({
      assetPair: {
        header: 'Asset/Market',
        renderCell: ({ borrowToken, collateralToken }) => (
          <div className="flex flex-col">
            <div className="flex items-center">
              <TokenWithLogo
                token={borrowToken}
                reserveStatus="active"
                isCombinedBalance={false}
                className="pb-0 font-medium text-base"
                hideName
              />
            </div>
            <Typography variant="prompt" className="ml-8">
              Collateralized by {collateralToken.symbol}
            </Typography>
          </div>
        ),
      },
      debt: {
        header: 'Debt',
        sortable: true,
        sortingFn: (a, b) =>
          sortByUsdValue(
            { token: a.original.borrowToken, value: a.original.borrowAmount },
            { token: b.original.borrowToken, value: b.original.borrowAmount },
            'value',
          ),
        headerAlign: 'right',
        showOnMobile: true,
        renderCell: ({ borrowToken, borrowAmount }, mobileViewOptions) => (
          <div className="flex flex-col items-center md:items-end">
            <CompactValueCell
              token={borrowToken}
              value={borrowAmount}
              mobileViewOptions={mobileViewOptions}
              hideEmpty
            />
          </div>
        ),
      },
      apy: {
        header: <ApyTooltip variant="borrow">APY</ApyTooltip>,
        headerAlign: 'right',
        sortable: true,
        showOnMobile: true,
        sortingFn: (a, b) => sortByAPY(a.original.borrowAPY, b.original.borrowAPY),
        renderCell: ({ borrowAPY, borrowToken, pairAddress }, mobileViewOptions) => {
          const cellColorOverride = chainConfig[CheckedAddress(pairAddress)]?.options?.cellColorOverrides?.borrow
          const points = getByAddress({
            type: 'isolated',
            action: 'borrow',
            address: pairAddress,
          })
          return (
            <div className="flex justify-center md:justify-end">
              <ApyWithRewardsCell
                action="borrow"
                asset={borrowToken.symbol}
                apy={borrowAPY}
                points={points}
                mobileViewOptions={mobileViewOptions}
                partnerPoints={chainConfig[CheckedAddress(pairAddress)]?.points}
                className={cn(cellColorOverride ? cellColorOverride : 'text-foreground')}
                pointsError={hasPointsError}
                pointsLoading={isPointsDataLoading}
              />
            </div>
          )
        },
      },
      actions: {
        header: '',
        renderCell: ({ chainId, pairAddress, borrowAmount }) => (
          <ActionsCellButton
            chainId={chainId}
            pairAddress={pairAddress}
            borrowAmount={borrowAmount}
            openDialog={openDialog}
          />
        ),
      },
    }),
    [openDialog, chainConfig, getByAddress, hasPointsError, isPointsDataLoading],
  )

  return (
    <Panel className="bg-panel xl:h-full md:px-3">
      <Panel.Header>
        <Panel.Title className="text-xl md:px-3" gradient>
          My borrows
        </Panel.Title>
      </Panel.Header>

      <Panel.Content>
        <ResponsiveDataTable
          gridTemplateColumnsClassName="grid-cols-[2fr_1fr_1fr_1fr] gap-4"
          columnDefinition={columnDefinition}
          data={assets}
          variant="isolated-positions"
          footer={
            assets.length === 0 ? (
              <TableRow className="border-none py-4 pl-0.5 text-sm">
                <TableCell>
                  <Typography variant="prompt" className="text-sm">
                    You have no positions yet
                  </Typography>
                </TableCell>
              </TableRow>
            ) : undefined
          }
        />
      </Panel.Content>
    </Panel>
  )
}

function ActionsCellButton({
  chainId,
  pairAddress,
  borrowAmount,
  openDialog,
}: { chainId: number; pairAddress: Address; borrowAmount: NormalizedUnitNumber; openDialog: OpenDialogFunction }) {
  const pairDetails = useIsolatedPairDetails({
    chainId,
    pairAddress,
  })

  return (
    <ActionsCell className="flex justify-end">
      <Button
        variant="primary"
        size="sm"
        className="w-full md:w-auto"
        disabled={borrowAmount.isZero()}
        onClick={() => {
          openDialog(repayDialogConfig, {
            walletOverview: pairDetails.walletOverview,
            pairOverview: pairDetails.pairOverview,
          })
        }}
      >
        Repay
      </Button>
    </ActionsCell>
  )
}
